import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  toast,
  Alert,
  AlertTitle,
  AlertDescription,
  Icon,
  Skeleton,
  Flex,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@kognitos/lattice';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { captureException } from '@/modules/sentry/helpers/capture';
import { errorHandler } from '@/shared/utils/error-handler';
import { getRoutePath } from '@/routes/util';
import {
  getListAutomationsInfiniteQueryKey,
  getListAutomationsQueryKey,
  getListAutomationSummariesInfiniteQueryKey,
  getListAutomationSummariesQueryKey,
  getQueryAutomationQueryKey,
  useForkPublishedAutomation,
  useQueryAutomation,
} from '@/generated/hooks/grimoire/v1/api';
import { V1AutomationStage } from '@/generated/hooks/grimoire/models';

interface IAutomationOpenDraftDialogProps {
  open: boolean;
  onClose: () => void;
  automationId: string;
  isOverride?: boolean;
  isLoading?: boolean;
  isDeactivated?: boolean;
  onDraftCreate?: () => void;
}

export function AutomationOpenDraftDialog({
  open,
  onClose,
  automationId,
  isOverride = false,
  isLoading,
  onDraftCreate,
  isDeactivated = false,
}: IAutomationOpenDraftDialogProps) {
  const { t: tAutomation } = useTranslation('automation');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);
  const queryClient = useQueryClient();

  const {
    mutate: forkPublishedAutomation,
    isPending: isForkingPublishedAutomation,
  } = useForkPublishedAutomation();

  // Only fetch the existing draft's display name when we actually need to
  // render the "open existing draft" link (override case, dialog open,
  // org/workspace resolved). Skipped entirely otherwise to avoid spurious
  // network calls.
  const shouldFetchExistingDraft = Boolean(
    open && isOverride && currentOrganizationId && currentWorkspaceId,
  );
  const { data: existingDraftData, isLoading: isExistingDraftLoading } =
    useQueryAutomation(
      currentOrganizationId,
      currentWorkspaceId,
      automationId,
      { stage: V1AutomationStage.AUTOMATION_STAGE_DRAFT },
      {
        query: {
          enabled: shouldFetchExistingDraft,
        },
      },
    );

  const existingDraftDisplayName = existingDraftData?.data?.display_name;

  const handleCreateDraft = () => {
    forkPublishedAutomation(
      {
        organization: currentOrganizationId,
        workspace: currentWorkspaceId,
        automation: automationId,
        data: {},
      },
      {
        onSuccess: () => {
          // Invalidate the workspace's automations list so the newly-forked
          // draft is visible when the user navigates back to /drafts.
          // Without this the Drafts page serves stale react-query cache
          // (the pre-fork list) and the new draft only appears on a hard
          // refresh — reported in OC-1281.
          queryClient.invalidateQueries({
            queryKey: getListAutomationsQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
            ),
          });
          queryClient.invalidateQueries({
            queryKey: getListAutomationSummariesQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
            ),
          });
          queryClient.invalidateQueries({
            queryKey: getListAutomationsInfiniteQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
            ),
          });
          queryClient.invalidateQueries({
            queryKey: getListAutomationSummariesInfiniteQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
            ),
          });
          // The editor may stay mounted on the way to the draft, so the old
          // draft this replaced can't be served from the cache.
          queryClient.invalidateQueries({
            queryKey: getQueryAutomationQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
              automationId,
              { stage: V1AutomationStage.AUTOMATION_STAGE_DRAFT },
            ),
          });
          onDraftCreate?.();
          onClose();
          navigate(
            getRoutePath('DRAFT_EDITOR', {
              orgId: currentOrganizationId,
              workspaceId: currentWorkspaceId,
              draftId: automationId,
            }),
          );
        },
        onError: error => {
          errorHandler(error, () => {
            toast.error(tAutomation('automation.draft.create.error'));
            captureException(error);
          });
        },
      },
    );
  };

  const renderExistingDraftLink = () => {
    const draftRoute = getRoutePath('DRAFT_EDITOR', {
      orgId: currentOrganizationId,
      workspaceId: currentWorkspaceId,
      draftId: automationId,
    });

    return (
      <Flex
        align="center"
        justify="between"
        gap={2}
        width="full"
        position="relative"
        paddingX={3}
        paddingY={2}
        className="rounded-md border border-border transition-colors hover:bg-accent focus-within:ring-2 focus-within:ring-ring"
        data-testid="automation-draft-existing-draft-link"
      >
        {isExistingDraftLoading ? (
          <Skeleton className="h-4 w-40" />
        ) : (
          // The name opens the draft here; its stretched hit area covers the card.
          <Link
            to={draftRoute}
            onClick={onClose}
            className="flex min-w-0 items-center gap-2 after:absolute after:inset-0 after:rounded-md focus-visible:outline-none"
            data-testid="automation-draft-existing-draft-link-same-window"
          >
            <span
              aria-hidden="true"
              className="size-2 shrink-0 rounded-full bg-success"
            />
            <Text level="small" weight="medium" className="truncate">
              {existingDraftDisplayName}
            </Text>
          </Link>
        )}
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <Link
              to={draftRoute}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={tAutomation(
                'automation.draft.create.existingDraft.openTooltip',
              )}
              className="relative shrink-0 text-muted-foreground hover:text-foreground"
              data-testid="automation-draft-existing-draft-link-open"
            >
              <Icon type="ArrowUpRight" size="sm" />
            </Link>
          </TooltipTrigger>
          <TooltipContent>
            {tAutomation('automation.draft.create.existingDraft.openTooltip')}
          </TooltipContent>
        </Tooltip>
      </Flex>
    );
  };

  const getNoteCopy = () => {
    if (isOverride) {
      return tAutomation('automation.draft.create.note.override');
    }
    if (isDeactivated) {
      return tAutomation('automation.draft.create.note.deactivated');
    }
    return tAutomation('automation.draft.create.note');
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Flex direction="column" gap={4} width="full">
          <Flex direction="column" gap={1.5} width="full">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-1/2" />
          </Flex>
          <Skeleton className="h-20 w-full" />
        </Flex>
      );
    }

    return (
      <Flex direction="column" gap={4} width="full">
        <DialogDescription>
          {isOverride
            ? tAutomation('automation.draft.create.overrideDescription')
            : tAutomation('automation.draft.create.description')}
        </DialogDescription>

        {isOverride && renderExistingDraftLink()}

        <Alert>
          <Icon type="Info" size="sm" />
          <AlertTitle>{tCommon('common.note')}</AlertTitle>
          <AlertDescription>{getNoteCopy()}</AlertDescription>
        </Alert>
      </Flex>
    );
  };

  const confirmLabel = isOverride
    ? tAutomation('automation.draft.create.actions.replaceDraft')
    : tAutomation('automation.openInDrafts');

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        data-testid="automation-draft-create-dialog"
        stopPropagation
      >
        <DialogHeader>
          <DialogTitle>{tAutomation('automation.openInDrafts')}</DialogTitle>
        </DialogHeader>

        {renderContent()}

        <DialogFooter>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isForkingPublishedAutomation}
            data-testid="automation-draft-create-dialog-cancel"
          >
            {tCommon('common.action.cancel')}
          </Button>
          <Button
            onClick={handleCreateDraft}
            disabled={isForkingPublishedAutomation || isLoading}
            isLoading={isForkingPublishedAutomation}
            data-testid="automation-draft-create-dialog-confirm"
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
